import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} muteButton - The button to toggle mute/unmute
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = true;

  /** @type {boolean} */
  #isPerformingAction = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, (event) => {
      // Pause this video if another video started playing (and we're not performing our own action)
      if (event.detail.target !== this && !this.#isPerformingAction) {
        this.pauseMedia();
        // Also mute it when paused by another video
        if (!this.isMuted) {
          this.muteMedia();
        }
      }
    }, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Position the controls overlay relative to parent immediately (PDP or FAQ context)
    const overlay = this.querySelector('.video-controls-overlay');
    // Try to find PDP context first, then FAQ context
    const positioningParent = this.closest('.product-media') || 
                              this.closest('.faq-item__video');
    
    if (overlay && positioningParent) {
      // Make positioning parent the positioning context
      positioningParent.style.position = 'relative';
      // Move overlay to be direct child of positioning parent
      positioningParent.appendChild(overlay);
      // Make overlay visible after positioning
      overlay.style.opacity = '1';
      
      // Re-bind click events after moving (component framework bindings are lost)
      const toggleButton = overlay.querySelector('[ref="toggleMediaButton"]');
      const muteButton = overlay.querySelector('[ref="muteButton"]');
      
      if (toggleButton) {
        toggleButton.addEventListener('click', () => this.toggleMedia(), { signal });
      }
      
      if (muteButton) {
        muteButton.addEventListener('click', () => this.toggleMute(), { signal });
      }
    }

    // Autoplay when deferred-media has autoplay attribute
    if (this.hasAttribute('autoplay')) {
      // Video is already rendered due to video_autoplay in liquid
      // Just set the playing state and update UI
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
      // Mark as loaded to prevent double-loading
      this.setAttribute('data-media-loaded', 'true');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    // Find button in its new location (may have been moved to product-media or faq-item__video)
    const toggleMediaButton = this.refs.toggleMediaButton || 
                              this.closest('.product-media, .faq-item__video')?.querySelector('[ref="toggleMediaButton"]');
    if (toggleMediaButton instanceof HTMLElement) {
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Updates the visual hint for mute/unmute state
   * @param {boolean} isMuted - Whether the video is currently muted
   */
  updateMuteHint(isMuted) {
    // Find button in its new location (may have been moved to product-media or faq-item__video)
    const muteButton = this.refs.muteButton || 
                       this.closest('.product-media, .faq-item__video')?.querySelector('[ref="muteButton"]');
    if (muteButton instanceof HTMLElement) {
      const volumeIcon = muteButton.querySelector('.icon-volume');
      const mutedIcon = muteButton.querySelector('.icon-volume-muted');
      if (volumeIcon) volumeIcon.classList.toggle('hidden', isMuted);
      if (mutedIcon) mutedIcon.classList.toggle('hidden', !isMuted);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  /**
   * Toggle mute/unmute state of the media
   */
  toggleMute() {
    if (this.isMuted) {
      this.unmuteMedia();
    } else {
      this.muteMedia();
    }
  }

  playMedia() {
    // Set flag to prevent this video from pausing itself
    this.#isPerformingAction = true;
    
    // Dispatch event to pause all other videos
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
    
    // Reset flag after a short delay
    setTimeout(() => { this.#isPerformingAction = false; }, 100);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Unmutes the media
   */
  unmuteMedia() {
    // Set flag to prevent this video from pausing itself
    this.#isPerformingAction = true;
    
    // When unmuting a playing video, pause all other videos (only one video should have audio)
    if (this.isPlaying) {
      this.dispatchEvent(new MediaStartedPlayingEvent(this));
    }
    
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"unMute","args":""}'
          : '{"method":"setVolume","value":1}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) video.muted = false;
    }
    this.isMuted = false;
    this.updateMuteHint(this.isMuted);
    
    // Reset flag after a short delay
    setTimeout(() => { this.#isPerformingAction = false; }, 100);
  }

  /**
   * Mutes the media
   */
  muteMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"mute","args":""}'
          : '{"method":"setVolume","value":0}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) video.muted = true;
    }
    this.isMuted = true;
    this.updateMuteHint(this.isMuted);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
